import { SubmissionError } from 'redux-form';
import { goToElement } from '../../../utils/goToElement';
import {
	COMPANIES_FORM_REQUIRED_FIELDS,
	DOCUMENTS_REQUIRED_FIELDS,
	REQUIRED_FIELDS_SETTINGS,
	REQUIRED_FIELDS,
} from './constants';

export const transformToValidationErrors = errors => {
	goToElement(Object.keys(errors)[0]);
	return Object.entries(errors).reduce((prev, [key, error]) => {
		const section = [key.split('.')[0]];

		const field = [key.split('.')[1]];

		if (!prev[section]) {
			prev[section] = {};
		}

		prev[section][field] = error;

		return prev;
	}, {});
};

export const validate = values => {
	REQUIRED_FIELDS.forEach(key => {
		const section = [key.split('.')[0]];

		const field = [key.split('.')[1]];

		if (!values?.[section]?.[field]) {
			goToElement(key);
			throw new SubmissionError({ [section]: { [field]: 'Заполните это поле' } });
		}

		if (values?.contact?.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values?.contact?.email)) {
			goToElement(key);
			throw new SubmissionError({ contact: { email: 'Не валидный email' } });
		}
	});
};

export const normalizePhone = value => {
	if (!value) {
		return value;
	}

	if (value === '+') {
		return '';
	}

	const onlyNums = value.replace(/[^\d]/g, '');

	if (onlyNums[0] !== '7' && onlyNums.length === 1) {
		return `+7${onlyNums}`;
	}

	if (onlyNums.length <= 4) {
		return `+7${onlyNums.slice(1, 4)}`;
	}

	if (onlyNums.length <= 7) {
		return `+7(${onlyNums.slice(1, 4)})${onlyNums.slice(4)}`;
	}

	if (onlyNums.length <= 9) {
		return `+7(${onlyNums.slice(1, 4)})${onlyNums.slice(4, 7)}-${onlyNums.slice(7)}`;
	}

	return `+7(${onlyNums.slice(1, 4)})${onlyNums.slice(4, 7)}-${onlyNums.slice(7, 9)}-${onlyNums.slice(9, 11)}`;
};

export const transformToCompanyValidationErrors = errors => {
	goToElement(Object.keys(errors)[0]);
	return Object.entries(errors).reduce((prev, [key, error]) => {
		if (key === 'transports' && Array.isArray(error)) {
			prev.transportsError = error;
		} else if (key.startsWith('transports.')) {
			const matches = key.match(/transports\.(\d+)\.(.+)/);
			if (matches) {
				const [, index, field] = matches;

				if (!prev.transports) {
					prev.transports = {};
				}

				if (!prev.transports[index]) {
					prev.transports[index] = {};
				}

				const fieldMapping = {
					car_type_id: 'car_type_id',
					tonnage_id: 'tonnage_id',
					quantity: 'quantity',
				};

				const formField = fieldMapping[field] || field;
				prev.transports[index][formField] = error;
			}
		} else if (key.includes('transports') && key !== 'transports') {
			const arr = key.replace('_', '.').split('.');

			if (!prev.transports) {
				prev.transports = {};
			}

			prev.transports = {
				...prev.transports,
				[arr[2]]: {
					...(prev.transports[arr[2]] || {}),
					[arr[3]]: error,
				},
			};
		} else if (key.includes('contact') && key !== 'contact') {
			const arr = key.split('.');

			if (!prev.contact) {
				prev.contact = {};
			}

			prev.contact = {
				...prev.contact,
				[arr[1]]: error,
			};
		} else if (key.includes('.')) {
			const section = key.split('.')[0];

			const field = key.split('.')[1];

			if (!prev[section]) {
				prev[section] = {};
			}

			prev[section][field] = error;
		} else {
			prev[key] = error;
		}

		return prev;
	}, {});
};

const getValueByPath = (obj, path) => {
	return path.split('.').reduce((current, key) => current?.[key], obj);
};

const setErrorByPath = (errors, path, message) => {
	const keys = path.split('.');
	let current = errors;

	for (let i = 0; i < keys.length - 1; i++) {
		const key = keys[i];
		if (!current[key]) {
			current[key] = {};
		}
		current = current[key];
	}

	current[keys[keys.length - 1]] = message;
};

export const validateAbout = values => {
	const firstMissingField = COMPANIES_FORM_REQUIRED_FIELDS.filter(key => key !== 'transports').find(key => {
		const val = getValueByPath(values, key);

		return !val || val.length === 0;
	});

	if (firstMissingField === 'delivery_methods') {
		const errors = {};
		setErrorByPath(errors, firstMissingField, 'Заполните это поле');
		throw new SubmissionError(errors);
	}

	if (values.contact?.email) {
		const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
		if (!emailRegex.test(values.contact.email)) {
			const errors = {};
			setErrorByPath(errors, 'contact.email', 'Не валидный email');
			throw new SubmissionError(errors);
		}
	}

	if (!Array.isArray(values.transports) || values.transports.length === 0) {
		throw new SubmissionError({
			transports: [
				{
					car_type_id: 'Заполните это поле',
				},
			],
		});
	}

	const transportErrors = [];
	let hasErrors = false;

	for (let idx = 0; idx < values.transports.length; idx++) {
		const transport = values.transports[idx];

		const isEmpty = !transport.car_type_id && !transport.tonnage_id && !transport.quantity;

		if (isEmpty || !transport.car_type_id) {
			transportErrors[idx] = { car_type_id: 'Заполните это поле' };
			hasErrors = true;
			break;
		}

		if (!transport.quantity) {
			transportErrors[idx] = { quantity: 'Заполните это поле' };
			hasErrors = true;
			break;
		}
	}

	if (hasErrors) {
		throw new SubmissionError({
			transports: transportErrors,
		});
	}

	if (firstMissingField && firstMissingField !== 'delivery_methods') {
		const errors = {};
		setErrorByPath(errors, firstMissingField, 'Заполните это поле');
		throw new SubmissionError(errors);
	}
};

const validateSettingDocuments = fields => {
	const documentErrors = { documents: [] };

	fields?.forEach((item, idx) => {
		DOCUMENTS_REQUIRED_FIELDS.forEach(key => {
			if (!item?.[key]) {
				if (!documentErrors?.documents?.[idx]) {
					documentErrors.documents[idx] = {};
				}
				documentErrors.documents[idx][key] = 'Заполните это поле';

				if (!documentErrors.documents[idx].hasScrolled) {
					goToElement(`documents[${idx}].${key}`);
					documentErrors.documents[idx].hasScrolled = true;
				}
			}
		});
	});

	return documentErrors;
};

export const validateSetting = values => {
	const errors = {};

	REQUIRED_FIELDS_SETTINGS.forEach(key => {
		if (key !== 'documents' && !values?.[key]) {
			errors[key] = 'Заполните это поле';
			goToElement(key);
		}
	});

	if (!errors.name && Object.keys(errors).length === 0) {
		const documentErrors = validateSettingDocuments(values?.documents);

		if (Object.keys(documentErrors.documents).length > 0) {
			Object.assign(errors, documentErrors);
		}
	}

	if (Object.keys(errors).length > 0) {
		throw new SubmissionError(errors);
	}

	return errors;
};

export const transformToValidationSettingErrors = (
	errors,
	{ normalizeDotToBrackets = false, replaceDocumentsNameToCustom = false } = {},
) => {
	if (!errors || typeof errors !== 'object') return {};

	const result = {};
	let hasScrolled = false;

	Object.entries(errors).forEach(([key, value]) => {
		let fieldKey = key;

		if (normalizeDotToBrackets) {
			fieldKey = fieldKey.replace(/\.(\d+)\./g, '[$1].');
		}

		if (!hasScrolled) {
			goToElement(fieldKey);
			hasScrolled = true;
		}

		const match = fieldKey.match(/^documents(?:\.|\[)(\d+)(?:\]?)\.(\w+)$/);
		if (match) {
			const [, indexStr, fieldRaw] = match;

			const index = parseInt(indexStr, 10);

			const field = replaceDocumentsNameToCustom && fieldRaw === 'name' ? 'name' : fieldRaw;

			if (!result.documents) result.documents = [];
			if (!result.documents[index]) result.documents[index] = {};

			result.documents[index][field] = Array.isArray(value) ? value[0] : value;
		} else {
			result[fieldKey] = Array.isArray(value) ? value[0] : value;
		}
	});

	return result;
};
